// API配置文件
import axios from "axios";
import { Message } from "element-ui";
import { config } from "@/config/env";

// API基础配置
const API_CONFIG = {
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
};

// 创建axios实例
const apiClient = axios.create(API_CONFIG);

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 添加token到请求头
    const token = localStorage.getItem("userToken");
    console.log("🔑 请求拦截器:", {
      url: config.url,
      method: config.method,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 10) + "..." : null
    });
    
    if (token) {
      // FastAdmin期望的是HTTP_TOKEN头，不是Authorization
      config.headers.Token = token;
      
      // 获取用户名用于token_key
      const userInfoStr = localStorage.getItem("userInfo");
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo.username) {
            config.headers.Username = userInfo.username;
            console.log("✅ 已添加Token头:", token.substring(0, 10) + "...");
            console.log("✅ 已添加Username头:", userInfo.username);
          }
        } catch (e) {
          console.warn("⚠️ 解析用户信息失败:", e);
        }
      }
    } else {
      console.warn("⚠️ 未找到token，请求可能失败");
    }

    // 添加时间戳防止缓存
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error) => {
    console.error("请求配置错误:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    const { data } = response;
    
    console.log("🔄 API响应:", {
      url: response.config.url,
      status: response.status,
      data: data
    });

    // 统一处理后端返回格式
    if (data.success !== undefined) {
      // 新的API格式: success=true/false
      if (data.success === true) {
        // 成功情况
        return data.data || data;
      } else {
        // success=false 表示失败
        console.warn("❌ API业务错误:", {
          url: response.config.url,
          code: data.code,
          message: data.message
        });
        const errorMessage = data.message || "请求失败";
        const error = new Error(errorMessage);
        error.apiResponse = data; // 保存原始响应数据
        error.isApiError = true; // 标记为API业务错误
        return Promise.reject(error);
      }
    }
    
    // 兼容旧的API格式
    if (data.code !== undefined) {
      // 根据你的API: 1=成功, 0=失败, 200=成功
      if (data.code === 1 || data.code === 200) {
        // 成功情况
        return data.data || data;
      } else {
        // code !== 1 且 !== 200 表示失败，不在拦截器中显示错误消息
        // 让具体的组件决定是否显示错误消息
        console.warn("❌ API业务错误:", {
          url: response.config.url,
          code: data.code,
          message: data.msg || data.message
        });
        const errorMessage = data.msg || data.message || "请求失败";
        const error = new Error(errorMessage);
        error.apiResponse = data; // 保存原始响应数据
        error.isApiError = true; // 标记为API业务错误
        return Promise.reject(error);
      }
    }

    return data;
  },
  (error) => {
    console.error("🚨 API请求错误:", {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });

    // 如果是API业务错误(code=0)，不在拦截器中显示消息
    if (error.isApiError) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      let message = "请求失败";

      switch (status) {
        case 400:
          message = "请求参数错误";
          break;
        case 401:
          message = "未授权访问";
          console.warn("🔒 检测到401未授权，清除登录状态并跳转登录页");
          localStorage.clear();
          window.location.href = "/login";
          break;
        case 403:
          message = "访问被禁止";
          break;
        case 404:
          message = "请求的资源不存在";
          break;
        case 500:
          message = "服务器内部错误";
          break;
        case 502:
          message = "网关错误";
          break;
        case 503:
          message = "服务不可用";
          break;
        default:
          message = data?.message || `请求失败 (${status})`;
      }

      // 对于登录请求，不自动显示错误消息，让组件自己处理
      const isLoginRequest = error.config?.url?.includes('/user/login');
      if (!isLoginRequest) {
        Message.error(message);
      }
    } else if (error.request) {
      // 对于登录请求，不自动显示错误消息
      const isLoginRequest = error.config?.url?.includes('/user/login');
      if (!isLoginRequest) {
        Message.error("网络连接失败，请检查网络设置");
      }
    } else {
      // 对于登录请求，不自动显示错误消息
      const isLoginRequest = error.config?.url?.includes('/user/login');
      if (!isLoginRequest) {
        Message.error("请求配置错误");
      }
    }

    return Promise.reject(error);
  }
);

// API端点配置
export const API_ENDPOINTS = {
  // 用户相关
  USER: {
    LOGIN: "/user/login",
    LOGOUT: "/user/logout",
    REGISTER: "/user/register",
    PROFILE: "/user/profile",
    UPDATE_PROFILE: "/user/update",
    RESET_PASSWORD: "/user/reset-password",
  },

  // 用户设置相关
  USER_SETTINGS: {
    GET: "/settings/getUserSettings",
    UPDATE: "/settings/updateUserSettings",
    RESET: "/settings/resetToDefault",
  },

  // 用户个人资料相关
  PROFILE: {
    GET: "/profile/getUserProfile",
    UPDATE: "/profile/updateUserProfile",
    CHANGE_PASSWORD: "/profile/changePassword",
    UPLOAD_AVATAR: "/profile/uploadAvatar"
  },

  // 复习相关
  REVIEW: {
    GET_TODAY_WORDS: "/review/getTodayReviewWords",
    SUBMIT_LEARNING: "/review/submitLearning"
  },

  // 自主测试相关接口
  SELF_TEST: {
    GET_QUESTIONS: "/test/getQuestions", // POST请求
    SUBMIT_TEST: "/test/submitTest", // POST请求
    GET_RECORDS: "/test/getTestRecords", // GET请求
    GET_STATS: "/test/getTestStats", // GET请求
  },

  // 单词相关
  WORD: {
    LIST: "/word/list",
    DETAIL: "/word/detail",
    SEARCH: "/word/search",
    ADD: "/word/add",
    UPDATE: "/word/update",
    DELETE: "/word/delete",
    BATCH_IMPORT: "/word/batch-import",
    EXPORT: "/word/export",
    CATEGORIES: "/word/categories",
    STATISTICS: "/word/statistics",
  },

  // 学习报告相关
  LEARNING_REPORT: {
    GET_REPORT: "/index/getLearningReport",
  },

  // 单词本相关（使用Index控制器）
  WORD_BOOK: {
    LIST: "/index/getWordList",
    STATISTICS: "/index/getWordStatistics",
  },

  // 学习进度相关
  PROGRESS: {
    GET_REVIEW_LIST: "/words/getReviewWords",
    SUBMIT_REVIEW: "/words/submitLearning",
    GET_STATISTICS: "/words/getUserStats",
    GET_DAILY_SUMMARY: "/progress/daily-summary",
    GET_LEARNING_REPORT: "/progress/learning-report",
    UPDATE_STAGE: "/words/updateUserStage",
  },

  // 系统相关
  SYSTEM: {
    GET_CONFIG: "/system/config",
    UPLOAD_FILE: "/system/upload",
    GET_DICTIONARY: "/system/dictionary",
  },

  // 课程相关
  COURSE: {
    LIST: "/course/getCourseList",
    MYLIST: "/course/getMyCourseList",
    DETAIL: "/course/getCourseDetail",
    START: "/course/startCourse",
    PROGRESS: "/course/updateProgress",
    UNITS: "/course/getUnits",
    UNIT_WORDS: "/course/getUnitWords",
    SUBMIT_UNIT_LEARNING: "/course/submitUnitLearning",
  },

  // 排行榜相关
  LEADERBOARD: {
    STAGE_LEADERBOARD: "/index/getStageLeaderboard",
    OVERVIEW: "/index/getLeaderboardOverview",
    USER_COMPLETIONS: "/index/getUserCompletions",
  },
};

export default apiClient;
